import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { User, Mail, Phone, MapPin, FileText, Plus, Trash2, Loader2, GraduationCap, Briefcase, Languages, Wrench, Camera, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadAuthFile } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [cv, setCv] = useState({ formazione: [], esperienze: [], competenze: [], lingue: [] });
  const [editSection, setEditSection] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [presetComp, setPresetComp] = useState([]);
  const [presetLingue, setPresetLingue] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editInfo, setEditInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/profilo`);
      setProfile(data);
      if (data?.foto_path) {
        try {
          const resp = await axios.get(`${API}/files/${data.foto_path}`, { 
            withCredentials: false, 
            responseType: 'blob',
            validateStatus: (status) => status === 200 
          });
          if (resp.status === 200) setPhotoUrl(URL.createObjectURL(resp.data));
        } catch (e) { /* foto non disponibile */ }
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchCV = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cv/me`);
      setCv(data || { formazione: [], esperienze: [], competenze: [], lingue: [] });
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchProfile(); fetchCV();
    axios.get(`${API}/preset/competenze`).then(r => setPresetComp(r.data || [])).catch(function(e){});
    axios.get(`${API}/preset/lingue`).then(r => setPresetLingue(r.data || [])).catch(function(e){});
  }, [fetchProfile, fetchCV]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/upload/foto-profilo`, fd);
      toast.success("Foto aggiornata!");
      fetchProfile();
    } catch { toast.error("Errore upload foto"); }
    setUploading(false);
  };

  const openEditInfo = () => {
    setInfoForm({ email: profile.email || "", telefono: profile.telefono || "", indirizzo: profile.indirizzo || "", data_nascita: profile.data_nascita || "", nazionalita: profile.nazionalita || "" });
    setEditInfo(true);
  };

  const saveInfo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profilo`, infoForm);
      toast.success("Dati aggiornati!");
      setEditInfo(false);
      fetchProfile();
    } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const saveFormazione = async () => {
    setSaving(true);
    const u = [...(cv.formazione || []), editForm];
    try { await axios.put(`${API}/cv/me`, { formazione: u }); setCv(p => ({ ...p, formazione: u })); setEditSection(null); toast.success("Aggiunto"); } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const saveEsperienza = async () => {
    setSaving(true);
    const u = [...(cv.esperienze || []), editForm];
    try { await axios.put(`${API}/cv/me`, { esperienze: u }); setCv(p => ({ ...p, esperienze: u })); setEditSection(null); toast.success("Aggiunto"); } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const saveLingua = async () => {
    setSaving(true);
    const u = [...(cv.lingue || []), editForm];
    try { await axios.put(`${API}/cv/me`, { lingue: u }); setCv(p => ({ ...p, lingue: u })); setEditSection(null); toast.success("Aggiunto"); } catch { toast.error("Errore"); }
    setSaving(false);
  };

  const removeItem = async (field, idx) => {
    const u = cv[field].filter((_, i) => i !== idx);
    await axios.put(`${API}/cv/me`, { [field]: u });
    setCv(p => ({ ...p, [field]: u }));
  };

  const addCompetenza = async (comp) => {
    if (!comp) return;
    const existing = cv.competenze || [];
    if (existing.includes(comp)) return;
    const u = [...existing, comp];
    await axios.put(`${API}/cv/me`, { competenze: u });
    setCv(p => ({ ...p, competenze: u }));
    toast.success("Competenza aggiunta");
  };

  const downloadCV = () => {
    downloadAuthFile(`${API}/cv/genera-pdf/${user?.id}`, `CV_${profile?.cognome || ''}_${profile?.nome || ''}.pdf`);
  };

  if (!profile) return <div className="text-center py-12 text-white/30">Caricamento profilo...</div>;

  const Section = ({ icon: Icon, title, children, onAdd, addLabel }) => (
    <div className="glass-card rounded-lg border border-white/8 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Icon className="w-4 h-4 text-[#00A0D1]" /> {title}
        </h3>
        {onAdd && <Button size="sm" variant="outline" onClick={onAdd} className="text-xs border-white/10 text-white/50 bg-transparent hover:bg-white/5"><Plus className="w-3 h-3 mr-1" /> {addLabel || "Aggiungi"}</Button>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="member-profile">
      {/* Profile Card with Photo */}
      <div className="glass-card rounded-lg border border-[#C9A84C]/20 p-6">
        <div className="flex items-start gap-5">
          <div className="relative group">
            {photoUrl ? (
              <img src={photoUrl} alt="Foto" className="w-20 h-20 rounded-xl object-cover border-2 border-[#C9A84C]/30" />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-[#C9A84C]/10 border-2 border-[#C9A84C]/20 flex items-center justify-center">
                <User className="w-8 h-8 text-[#C9A84C]" />
              </div>
            )}
            <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
              {uploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
              <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="upload-photo" />
            </label>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{profile.nome} {profile.cognome}</h2>
              <Badge className="bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/20 text-xs">{profile.ruolo}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
              <div className="flex items-center gap-2 text-sm text-white/50"><FileText className="w-3.5 h-3.5 text-white/25" /><span className="font-mono text-xs">{profile.codice_fiscale}</span></div>
              {profile.email && <div className="flex items-center gap-2 text-sm text-white/50"><Mail className="w-3.5 h-3.5 text-white/25" />{profile.email}</div>}
              {profile.telefono && <div className="flex items-center gap-2 text-sm text-white/50"><Phone className="w-3.5 h-3.5 text-white/25" />{profile.telefono}</div>}
              {profile.sezione && <div className="flex items-center gap-2 text-sm text-white/50"><MapPin className="w-3.5 h-3.5 text-white/25" />Sezione: {profile.sezione}</div>}
              {profile.data_nascita && <div className="text-xs text-white/40">Nascita: {profile.data_nascita}</div>}
              {profile.nazionalita && <div className="text-xs text-white/40">Nazionalità: {profile.nazionalita}</div>}
              {profile.indirizzo && <div className="text-xs text-white/40 col-span-2">Indirizzo: {profile.indirizzo}</div>}
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={downloadCV} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 text-xs" data-testid="download-cv-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Scarica CV (PDF)
              </Button>
              <Button size="sm" onClick={openEditInfo} variant="outline" className="border-white/15 text-white/60 bg-transparent hover:bg-white/5 text-xs" data-testid="edit-info-btn">
                <Plus className="w-3 h-3 mr-1" /> Modifica Dati
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Formazione */}
      <Section icon={GraduationCap} title="Formazione" onAdd={() => { setEditSection("formazione"); setEditForm({ titolo: "", istituto: "", anno_inizio: "", anno_fine: "" }); }}>
        {!cv.formazione?.length && <p className="text-sm text-white/25">Nessuna formazione</p>}
        <div className="space-y-2">{cv.formazione?.map((f, i) => (
          <div key={i} className="flex items-start justify-between p-3 bg-white/[0.02] rounded-md border border-white/5">
            <div><p className="text-sm font-medium text-white/80">{f.titolo}</p><p className="text-xs text-white/30">{f.istituto} | {f.anno_inizio} - {f.anno_fine || "In corso"}</p></div>
            <Button size="sm" variant="ghost" onClick={() => removeItem("formazione", i)} className="text-white/20 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}</div>
      </Section>

      {/* Esperienze */}
      <Section icon={Briefcase} title="Esperienze Lavorative" onAdd={() => { setEditSection("esperienza"); setEditForm({ ruolo: "", azienda: "", anno_inizio: "", anno_fine: "", descrizione: "" }); }}>
        {!cv.esperienze?.length && <p className="text-sm text-white/25">Nessuna esperienza</p>}
        <div className="space-y-2">{cv.esperienze?.map((e, i) => (
          <div key={i} className="flex items-start justify-between p-3 bg-white/[0.02] rounded-md border border-white/5">
            <div><p className="text-sm font-medium text-white/80">{e.ruolo}</p><p className="text-xs text-white/30">{e.azienda} | {e.anno_inizio} - {e.anno_fine || "In corso"}</p>{e.descrizione && <p className="text-xs text-white/20 mt-1">{e.descrizione}</p>}</div>
            <Button size="sm" variant="ghost" onClick={() => removeItem("esperienze", i)} className="text-white/20 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}</div>
      </Section>

      {/* Competenze con Preset */}
      <Section icon={Wrench} title="Competenze">
        <div className="flex flex-wrap gap-2 mb-3">{(cv.competenze || []).map((c, i) => (
          <Badge key={i} className="bg-[#00A0D1]/10 text-[#00A0D1] border border-[#00A0D1]/20 text-xs cursor-pointer hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20" onClick={() => removeItem("competenze", i)}>{c} &times;</Badge>
        ))}</div>
        <div className="mb-2">
          <select
            data-testid="competenza-select"
            className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-3 focus:border-[#00A0D1] focus:outline-none"
            value=""
            onChange={e => { if (e.target.value) addCompetenza(e.target.value); }}
          >
            <option value="" className="bg-[#0d1b2e]">Seleziona competenza...</option>
            {presetComp.filter(c => !(cv.competenze || []).includes(c)).map(c => (
              <option key={c} value={c} className="bg-[#0d1b2e]">{c}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Oppure scrivi..." className="text-sm bg-white/5 border-white/10 text-white placeholder:text-white/20"
            onKeyDown={e => { if (e.key === 'Enter' && e.target.value) { addCompetenza(e.target.value); e.target.value = ''; }}} data-testid="competenza-input" />
        </div>
      </Section>

      {/* Lingue con Livelli */}
      <Section icon={Languages} title="Lingue" onAdd={() => { setEditSection("lingua"); setEditForm({ nome: "", parlato: 5, scritto: 5, letto: 5 }); }}>
        {!cv.lingue?.length && <p className="text-sm text-white/25">Nessuna lingua</p>}
        <div className="space-y-3">{cv.lingue?.map((l, i) => {
          if (typeof l === 'string') return (
            <div key={i} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-md border border-white/5">
              <span className="text-sm text-white/60">{l}</span>
              <Button size="sm" variant="ghost" onClick={() => removeItem("lingue", i)} className="text-white/20 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
            </div>
          );
          return (
            <div key={i} className="p-3 bg-white/[0.02] rounded-md border border-white/5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-white/80">{l.nome}</p>
                <Button size="sm" variant="ghost" onClick={() => removeItem("lingue", i)} className="text-white/20 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[{k:"parlato",l:"Parlato"},{k:"scritto",l:"Scritto"},{k:"letto",l:"Lettura"}].map(s => (
                  <div key={s.k}>
                    <p className="text-[10px] text-white/30 mb-1">{s.l}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-[#C9A84C] rounded-full" style={{ width: `${(l[s.k] || 0) * 10}%` }} />
                      </div>
                      <span className="text-xs text-[#C9A84C] font-bold w-5 text-right">{l[s.k]}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}</div>
      </Section>

      {/* Formazione Dialog */}
      <Dialog open={editSection === "formazione"} onOpenChange={() => setEditSection(null)}>
        <DialogContent className="sm:max-w-md bg-[#0d1b2e] border-white/10 text-white">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Aggiungi Formazione</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-white/60">Titolo *</Label><Input data-testid="formazione-titolo" value={editForm.titolo||""} onChange={e=>setEditForm(p=>({...p,titolo:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-xs text-white/60">Istituto</Label><Input value={editForm.istituto||""} onChange={e=>setEditForm(p=>({...p,istituto:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Anno Inizio</Label><Input value={editForm.anno_inizio||""} onChange={e=>setEditForm(p=>({...p,anno_inizio:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-xs text-white/60">Anno Fine</Label><Input value={editForm.anno_fine||""} onChange={e=>setEditForm(p=>({...p,anno_fine:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditSection(null)} className="border-white/10 text-white/50 bg-transparent">Annulla</Button>
            <Button onClick={saveFormazione} disabled={saving} className="bg-[#C9A84C] text-[#001830]">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Esperienza Dialog */}
      <Dialog open={editSection === "esperienza"} onOpenChange={() => setEditSection(null)}>
        <DialogContent className="sm:max-w-md bg-[#0d1b2e] border-white/10 text-white">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Aggiungi Esperienza</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-white/60">Ruolo *</Label><Input value={editForm.ruolo||""} onChange={e=>setEditForm(p=>({...p,ruolo:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-xs text-white/60">Azienda</Label><Input value={editForm.azienda||""} onChange={e=>setEditForm(p=>({...p,azienda:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Anno Inizio</Label><Input value={editForm.anno_inizio||""} onChange={e=>setEditForm(p=>({...p,anno_inizio:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-xs text-white/60">Anno Fine</Label><Input value={editForm.anno_fine||""} onChange={e=>setEditForm(p=>({...p,anno_fine:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            </div>
            <div><Label className="text-xs text-white/60">Descrizione</Label><Textarea value={editForm.descrizione||""} onChange={e=>setEditForm(p=>({...p,descrizione:e.target.value}))} className="mt-1 bg-white/5 border-white/10 text-white" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditSection(null)} className="border-white/10 text-white/50 bg-transparent">Annulla</Button>
            <Button onClick={saveEsperienza} disabled={saving} className="bg-[#C9A84C] text-[#001830]">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lingua Dialog con Slider */}
      <Dialog open={editSection === "lingua"} onOpenChange={() => setEditSection(null)}>
        <DialogContent className="sm:max-w-md bg-[#0d1b2e] border-white/10 text-white" data-testid="lingua-dialog">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Aggiungi Lingua</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-white/60">Lingua *</Label>
              <select
                data-testid="lingua-select"
                className="w-full h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-3 mt-1 focus:border-[#00A0D1] focus:outline-none"
                value={editForm.nome || ""}
                onChange={e => setEditForm(p => ({ ...p, nome: e.target.value }))}
              >
                <option value="" className="bg-[#0d1b2e]">Seleziona lingua...</option>
                {presetLingue.map(l => <option key={l} value={l} className="bg-[#0d1b2e]">{l}</option>)}
              </select>
            </div>
            {[{k:"parlato",l:"Parlato"},{k:"scritto",l:"Scritto"},{k:"letto",l:"Lettura"}].map(s => (
              <div key={s.k}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-white/60">{s.l}</Label>
                  <span className="text-sm font-bold text-[#C9A84C]">{editForm[s.k] || 5}/10</span>
                </div>
                <input type="range" min={1} max={10} step={1} value={editForm[s.k] || 5}
                  onChange={e => setEditForm(p => ({ ...p, [s.k]: parseInt(e.target.value) }))}
                  className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#C9A84C]"
                  data-testid={`slider-${s.k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditSection(null)} className="border-white/10 text-white/50 bg-transparent">Annulla</Button>
            <Button onClick={saveLingua} disabled={saving || !editForm.nome} className="bg-[#C9A84C] text-[#001830]" data-testid="save-lingua">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modifica Info Personali Dialog */}
      <Dialog open={editInfo} onOpenChange={setEditInfo}>
        <DialogContent className="sm:max-w-md bg-[#13243d] border-[#1e3456] text-white" data-testid="edit-info-dialog">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>Modifica Dati Personali</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Email</Label><Input value={infoForm.email||""} onChange={e=>setInfoForm(p=>({...p,email:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
              <div><Label className="text-xs text-white/60">Telefono</Label><Input value={infoForm.telefono||""} onChange={e=>setInfoForm(p=>({...p,telefono:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white/60">Data di Nascita</Label><Input type="date" value={infoForm.data_nascita||""} onChange={e=>setInfoForm(p=>({...p,data_nascita:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white"/></div>
              <div><Label className="text-xs text-white/60">Nazionalità</Label><Input value={infoForm.nazionalita||""} onChange={e=>setInfoForm(p=>({...p,nazionalita:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white" placeholder="Italiana"/></div>
            </div>
            <div><Label className="text-xs text-white/60">Indirizzo</Label><Input value={infoForm.indirizzo||""} onChange={e=>setInfoForm(p=>({...p,indirizzo:e.target.value}))} className="mt-1 bg-white/5 border-[#1e3456] text-white" placeholder="Via, Citta, CAP"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditInfo(false)} className="border-[#1e3456] text-white/50 bg-transparent">Annulla</Button>
            <Button onClick={saveInfo} disabled={saving} className="bg-[#C9A84C] text-[#001830]">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
